import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import * as XLSX from "xlsx";
import { AflApiResponse } from "../../../types";
import { Button, Col, Form, FormControl, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoaderExportFileKirkosModal from "../../../components/LoaderExportFileKirkosModal";
import ConfirmationModal from "../../../components/ConfirmationModal";

const ExportAllStatus = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [allSearchResults, setAllSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  // Reset dates and table data when search query changes
  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
    setAllSearchResults([]);
    setHasSearched(false);
  }, [searchQuery]);

  const handleSearch = async () => {
    // Prepare payload with status as array or string based on selection
    const payload: any = {
      start_date: startDate?.toLocaleDateString("en-CA"),
      end_date: endDate?.toLocaleDateString("en-CA"),
    };

    // Always send statuses as an array
    if (searchQuery === "BOOKED") {
      payload.statuses = ["BOOKED", "ORDER_ACCEPT"];
    } else {
      payload.statuses = [searchQuery];
    }

    try {
      setIsLoading(true);
      let page = 0;
      let allData: any[] = [];
      let keepFetching = true;

      while (keepFetching) {
        const response = await axiosInstance.post(
          `${API_URL.EXPORTLIST_SEARCH_RESULTS}?size=50&page=${page}`,
          payload
        );

        const pageData = response?.data?.content || [];
        if (pageData.length === 0) {
          keepFetching = false;
        } else {
          allData = [...allData, ...pageData];
          page++;
        }
      }

      setAllSearchResults(allData);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching search results", error);
      setHasSearched(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (!allSearchResults || allSearchResults.length === 0) return;

    const wsData = allSearchResults.map((order, index) => {
      return {
        "S.No": index + 1,
        OrderCreatedAt: formatDate(order?.createdAt) || "N/A",
        orderId: order?.orderId || "N/A",
        ErpServiceNumber: order?.erp_service_number || "N/A",
        RentalDays: order?.rental_days || "N/A",
        customerName: order?.customer_name || "N/A",
        CustomerPhoneNo: order?.customer_phone_number || "N/A",
        ProductName: order?.products?.[0]?.productName || "N/A",
        Location: order?.delivery_location?.address || "N/A",
        ExtendedRentalDays: order?.extended_rental_days || "N/A",
        PlateNumber: order?.plate_number || "N/A",
        DeliveryDate: formatDate(order?.delivery_date) || "N/A",
        RecollectionDate: formatDate(order?.recollection_date) || "N/A",
        OrderStatus: order?.orderStatus || "N/A",
      };
    });

    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    const currentDate = new Date().toISOString().split("T")[0];
    const currentTime = new Date()
      .toLocaleTimeString("en-US", { hour12: false })
      .replace(/:/g, "_");
    const filename = `ServiceList-${currentDate}_${currentTime}_${searchQuery}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  const ordersToDisplay = useMemo(() => {
    return hasSearched ? allSearchResults : [];
  }, [allSearchResults, hasSearched]);

  return (
    <div className="row">
      <div className="col-md-12 mb-3 mt-2">
        <Form.Group as={Row} className="align-items-center">
          <Col sm="3">
            <Form.Select
              className="custom-select"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="BOOKED">Customer Orders</option>
              <option value="MERCHANT_ORDER_ACCEPT">Accepted Orders</option>
              <option value="DELIVERED">Delivered Orders</option>
              <option value="RECOLLECTED">Recollected Orders</option>
            </Form.Select>
          </Col>
          <Col sm="5" className="d-flex gap-2 align-items-center">
            <DatePicker
              selected={startDate}
              onChange={(date: Date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate || new Date()}
              className="search-bar-export"
              dateFormat="dd-MM-yyyy"
              placeholderText="DD-MM-YYYY"
              customInput={
                <FormControl
                  value={
                    startDate
                      ? `${String(startDate.getDate()).padStart(
                          2,
                          "0"
                        )}-${String(startDate.getMonth() + 1).padStart(
                          2,
                          "0"
                        )}-${startDate.getFullYear()}`
                      : ""
                  }
                  readOnly
                  className="input-date"
                />
              }
            />
            <span>to</span>
            <DatePicker
              selected={endDate}
              onChange={(date: Date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              className="search-bar-export"
              dateFormat="dd-MM-yyyy"
              placeholderText="DD-MM-YYYY"
              customInput={
                <FormControl
                  value={
                    endDate
                      ? `${String(endDate.getDate()).padStart(2, "0")}-${String(
                          endDate.getMonth() + 1
                        ).padStart(2, "0")}-${endDate.getFullYear()}`
                      : ""
                  }
                  readOnly
                  className="input-date"
                />
              }
            />
          </Col>
          <Col sm="2">
            <Button
              className="btn all_button_style text-black my-1"
              onClick={handleSearch}
              disabled={!searchQuery || !startDate || !endDate}
            >
              Search
            </Button>
          </Col>
          <Col sm="2">
            <Button
              className="btn all_button_style text-black my-1"
              onClick={handleExport}
              disabled={!hasSearched || isLoading}
            >
              Export
            </Button>
          </Col>
        </Form.Group>
      </div>

      <div className="col-md-12">
        <LoaderExportFileKirkosModal show={isLoading} />
      </div>

      <div className="col-12 dashboard-table-div">
        <table className="table table-hover dashboard-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Order Created Date</th>
              <th>Order Number</th>
              <th>LPO No</th>
              <th>Number of Days</th>
              <th>Customer Name</th>
              <th>Phone Number</th>
              <th>Product Name</th>
              <th>Location</th>
              <th>Extended days</th>
              <th>Plate Number</th>
              <th>Delivery Date and Time</th>
              <th>Recollection Date and Time</th>
              <th>Order Status</th>
            </tr>
          </thead>
          <tbody>
            {hasSearched && ordersToDisplay.length === 0 && (
              <tr>
                <td colSpan={14} className="text-center py-4">
                  No search results found
                </td>
              </tr>
            )}
            {ordersToDisplay.map((order: any, index: number) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{formatDate(order?.createdAt)}</td>
                <td>{order?.orderId}</td>
                <td>{order?.erp_service_number}</td>
                <td>{order?.rental_days}</td>
                <td>{order?.customer_name}</td>
                <td>{order?.customer_phone_number}</td>
                <td>{order?.products?.[0]?.productName}</td>
                <td>{order?.delivery_location?.address}</td>
                <td>{order?.extended_rental_days}</td>
                <td>{order?.plate_number}</td>
                <td>{formatDate(order?.delivery_date)}</td>
                <td>{formatDate(order?.recollection_date)}</td>
                <td>{order?.orderStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={false}
        onClose={() => {}}
        onConfirm={() => {}}
        message=""
      />
    </div>
  );
};

export default ExportAllStatus;
